// Shared Aviah admin UI primitives — carbon/ivory/champagne control center.
export const Card = ({ children, className = "", ...rest }) => (
  <div {...rest} className={`rounded-[3px] border border-[#171717]/10 bg-white p-4 sm:p-6 ${className}`}>{children}</div>
);

export const PageHead = ({ title, sub, actions }) => (
  <div className="mb-6 flex flex-wrap items-end justify-between gap-4 sm:mb-8">
    <div>
      <h1 className="text-xl font-light tracking-tight text-[#171717] sm:text-2xl">{title}</h1>
      {sub && <p className="mt-1 text-sm text-[#171717]/55">{sub}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-3">{actions}</div>}
  </div>
);

const PILL_TONES = {
  champagne: "border-[#C6A46A]/60 bg-[#C6A46A]/10 text-[#8a6f3e]",
  green: "border-emerald-600/30 bg-emerald-50 text-emerald-700",
  gray: "border-[#171717]/15 bg-[#171717]/5 text-[#171717]/60",
  red: "border-red-300 bg-red-50 text-red-700",
  blue: "border-sky-300 bg-sky-50 text-sky-700",
};

export const Pill = ({ tone = "gray", children }) => (
  <span className={`inline-block rounded-[2px] border px-2 py-0.5 text-[0.6rem] font-medium uppercase tracking-[0.14em] ${PILL_TONES[tone]}`}>
    {children}
  </span>
);

export const Field = ({ label, hint, children }) => (
  <div>
    <label className="mb-1.5 block text-[0.62rem] font-medium uppercase tracking-[0.16em] text-[#171717]/55">
      {label}
    </label>
    {children}
    {hint && <p className="mt-1 text-[0.68rem] text-[#171717]/40">{hint}</p>}
  </div>
);

export const Input = (props) => (
  <input
    {...props}
    className={`w-full min-h-[44px] rounded-[3px] border border-[#171717]/15 bg-white px-3.5 py-2.5 text-sm text-[#171717] transition-colors placeholder:text-[#171717]/30 focus:border-[#C6A46A] focus:outline-none ${props.className || ""}`}
  />
);

export const Textarea = (props) => (
  <textarea
    {...props}
    className={`w-full rounded-[3px] border border-[#171717]/15 bg-white px-3.5 py-2.5 text-sm leading-relaxed text-[#171717] transition-colors placeholder:text-[#171717]/30 focus:border-[#C6A46A] focus:outline-none ${props.className || ""}`}
  />
);

export const Select = (props) => (
  <select
    {...props}
    className={`min-h-[44px] rounded-[3px] border border-[#171717]/15 bg-white px-3 py-2.5 text-sm text-[#171717] transition-colors focus:border-[#C6A46A] focus:outline-none ${props.className || ""}`}
  />
);

export const Btn = ({ primary, danger, className = "", ...props }) => (
  <button
    {...props}
    className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-[3px] border px-4 py-2.5 text-[0.65rem] font-medium uppercase tracking-[0.16em] transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-50 ${
      primary
        ? "border-[#C6A46A] bg-[#C6A46A] text-[#171717] hover:bg-transparent hover:text-[#8a6f3e]"
        : danger
          ? "border-red-300 text-red-700 hover:bg-red-50"
          : "border-[#171717]/20 text-[#171717]/75 hover:border-[#C6A46A] hover:text-[#8a6f3e]"
    } ${className}`}
  />
);

// Bottom sheet on phones, centered dialog on desktop.
export const Confirm = ({ open, title, body, confirmLabel = "Confirm", onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-end bg-[#171717]/60 backdrop-blur-sm sm:items-center sm:p-4" role="dialog" aria-modal="true">
      <div className="w-full rounded-t-xl border border-[#171717]/10 bg-white p-7 pb-[calc(1.75rem+env(safe-area-inset-bottom))] shadow-2xl sm:max-w-md sm:rounded-[3px] sm:pb-7">
        <h3 className="text-lg font-light tracking-tight text-[#171717]">{title}</h3>
        <p className="mt-3 text-sm leading-relaxed text-[#171717]/60">{body}</p>
        <div className="mt-7 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Btn onClick={onCancel} className="w-full sm:w-auto">Cancel</Btn>
          <Btn primary onClick={onConfirm} className="w-full sm:w-auto">{confirmLabel}</Btn>
        </div>
      </div>
    </div>
  );
};

export const Empty = ({ children }) => (
  <div className="rounded-[3px] border border-dashed border-[#171717]/20 bg-white/60 px-6 py-14 text-center">
    <p className="text-[0.65rem] font-medium uppercase tracking-[0.2em] text-[#C6A46A]">No data yet</p>
    <p className="mt-2 text-sm text-[#171717]/55">{children}</p>
  </div>
);

// Small brand-colored loading state.
export const Loader = ({ label = "Loading" }) => (
  <div className="flex items-center gap-3 py-8" role="status" aria-live="polite">
    <span className="h-2 w-2 animate-pulse rounded-full bg-[#C6A46A]" />
    <span className="text-[0.65rem] uppercase tracking-[0.2em] text-[#171717]/45">{label}…</span>
  </div>
);
